import { computed, onMounted, onUnmounted, Ref } from 'vue';
import { debounce } from 'lodash';
import { Task } from '@/ui/concerns';

interface UseInfiniteScrollArgs<T = unknown> {
  containerSelector: string;
  task: Task;
  totalPages: Ref<number>;
  currentPage: Ref<number>;
  debounceWait?: number;
  bottomOffset?: number;
  resources?: Ref<T[]>;
}

export function useInfiniteScroll({
  containerSelector,
  task,
  totalPages,
  currentPage,
  debounceWait = 100,
  bottomOffset = 200,
  resources,
}: UseInfiniteScrollArgs) {
  const loading = computed(() => task.state.value.pending);

  let container: Element | Window;

  const isWindow = () => containerSelector === 'window';

  const isNotAtLastPage = () => currentPage.value < totalPages.value;

  const fetchResults = () => {
    let isAtBottomContainer: boolean;

    if (isWindow()) {
      isAtBottomContainer = (window.innerHeight + window.scrollY) >= document.body.scrollHeight - bottomOffset;
    } else {
      const _container = container as HTMLElement;

      isAtBottomContainer = _container.scrollTop + _container.clientHeight === _container.scrollHeight;
    }

    if (isAtBottomContainer && !loading.value && isNotAtLastPage()) {
      currentPage.value += 1;
      task.run();
    }
  };

  const handlerDebounced = debounce(fetchResults, debounceWait);

  const onFilterChange = () => {
    currentPage.value = 1;
    totalPages.value = 1;

    if (resources) {
      resources.value = [];
    }
  };

  onMounted(() => {
    if (isWindow()) {
      container = window;
    } else {
      container = document.querySelector(containerSelector)!;
    }

    container.addEventListener('scroll', handlerDebounced);
  });

  onUnmounted(() => {
    container.removeEventListener('scroll', handlerDebounced);
  });

  return {
    onFilterChange,
  };
}
